import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TID } from "@/lib/testIds";
import { SITE_IMAGES } from "@/data/images";

const CTA_IMG = SITE_IMAGES.cta;

// GoHighLevel embedded form
const GHL_FORM_SRC =
  "https://api.leadconnectorhq.com/widget/form/woVlNnrlDNjIUvTakhhg";
const GHL_FORM_ID = "inline-woVlNnrlDNjIUvTakhhg";
const GHL_SCRIPT_SRC = "https://link.msgsndr.com/js/form_embed.js";

function ensureGhlScript() {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[src="${GHL_SCRIPT_SRC}"]`)) return;
  const s = document.createElement("script");
  s.src = GHL_SCRIPT_SRC;
  s.async = true;
  document.body.appendChild(s);
}

export default function FinalCTA({
  open,
  setOpen,
  onOpenAudience,
}) {
  useEffect(() => {
    ensureGhlScript();
  }, []);

  return (
    <section
      id="cta"
      data-testid={TID.cta.root}
      className="relative h-[100svh] min-h-[680px] w-full overflow-hidden bg-charcoal text-cream"
    >
      <img
        src={CTA_IMG}
        alt="Luxury kitchen warmed by sunset light"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-charcoal/20" />
      <div className="relative h-full flex flex-col justify-end px-6 md:px-12 pb-20 md:pb-28">
        <div className="max-w-5xl">
          <p className="text-[11px] tracking-[0.32em] uppercase text-cream/70 mb-8">
            The Beginning
          </p>
          <h2 className="font-display font-light text-[12vw] md:text-[6.8vw] leading-[0.95] tracking-tightest">
            Let's build something <span className="italic text-brass">exceptional.</span>
          </h2>
          <p className="mt-8 max-w-2xl text-cream/85 text-lg md:text-xl">
            Whether you're creating your dream home or delivering your next
            development, we're ready to bring your vision to life.
          </p>
          <div className="mt-12 flex flex-wrap gap-4">
            <button
              data-testid={TID.cta.openBtn}
              onClick={() => (onOpenAudience ? onOpenAudience("homeowner") : setOpen(true))}
              className="inline-flex items-center gap-3 bg-cream text-charcoal hover:bg-brass px-8 md:px-10 py-5 md:py-6 text-[13px] tracking-[0.28em] uppercase transition-all duration-500"
            >
              Book a design consultation
              <span>→</span>
            </button>
            <button
              data-testid={TID.cta.tradeBtn + "-section"}
              onClick={() => (onOpenAudience ? onOpenAudience("builder") : setOpen(true))}
              className="inline-flex items-center gap-3 border border-cream/60 text-cream hover:border-brass hover:text-brass px-8 md:px-10 py-5 md:py-6 text-[13px] tracking-[0.28em] uppercase transition-all duration-500"
            >
              Discuss your next project
              <span>→</span>
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-charcoal/85 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-2xl bg-cream text-charcoal max-h-[92vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between px-6 md:px-10 pt-7 pb-5 border-b border-stone shrink-0">
                <div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-brassDark">
                    Begin
                  </p>
                  <h3 className="mt-2 font-display font-light text-2xl md:text-3xl leading-tight">
                    Tell us about your project.
                  </h3>
                </div>
                <button
                  data-testid={TID.cta.closeBtn}
                  onClick={() => setOpen(false)}
                  className="text-charcoal/60 hover:text-charcoal text-[11px] tracking-[0.22em] uppercase border border-charcoal/20 hover:border-charcoal px-3 py-2 transition-colors"
                  aria-label="Close"
                >
                  Close ✕
                </button>
              </div>

              <div
                data-testid={TID.cta.form}
                className="flex-1 overflow-y-auto px-2 md:px-4 pb-4"
              >
                <iframe
                  src={GHL_FORM_SRC}
                  id={GHL_FORM_ID}
                  title="Mirabilis consultation form"
                  loading="lazy"
                  style={{
                    width: "100%",
                    minHeight: "700px",
                    height: "936px",
                    border: "none",
                    background: "transparent",
                    display: "block",
                  }}
                  data-layout="{'id':'INLINE'}"
                  data-trigger-type="alwaysShow"
                  data-trigger-value=""
                  data-activation-type="alwaysActivated"
                  data-activation-value=""
                  data-deactivation-type="neverDeactivate"
                  data-deactivation-value=""
                  data-form-name="Kitchen form 1"
                  data-height="936"
                  data-layout-iframe-id={GHL_FORM_ID}
                  data-form-id="woVlNnrlDNjIUvTakhhg"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
